import React, { Component, ChangeEvent } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from "@material-ui/core";
import { hideDialog } from "../SharedComponents/Dialog";
import {
  LEAVE_REASON_OPTIONS,
  MAX_LEAVE_MESSAGE_LENGTH,
  LeaveReasonKey,
  LeaveGamePayload,
} from "../../types/gameLeave";

interface Props extends WithStyles<typeof styles> {
  onConfirm: (payload: LeaveGamePayload, callback: (error?: unknown) => void) => void;
  onCancel?: () => void;
}

interface State {
  selected: Record<string, boolean>;
  message: string;
  error: string;
  submitting: boolean;
}

class LeaveGameReasonDialog extends Component<Props, State> {
  private readonly requestId = createLeaveRequestId();

  state: State = {
    selected: {},
    message: "",
    error: "",
    submitting: false,
  };

  toggle = (key: string) => (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    this.setState(prev => ({
      selected: { ...prev.selected, [key]: checked },
      error: "",
    }));
  };

  onMessage = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ message: e.target.value, error: "" });
  };

  onConfirm = () => {
    const reasons = LEAVE_REASON_OPTIONS.map(o => o.key).filter(k => this.state.selected[k]) as LeaveReasonKey[];
    const message = this.state.message.trim();
    if (reasons.length === 0) {
      this.setState({ error: "Select at least one reason to leave." });
      return;
    }
    if (reasons.includes("other") && !message) {
      this.setState({ error: "Please type a message when selecting Other." });
      return;
    }
    this.setState({ submitting: true, error: "" });
    this.props.onConfirm({ reasons, message: message || undefined, requestId: this.requestId }, error => {
      if (error) {
        const serverError =
          (error as any)?.response?.data?.error || (error as any)?.response?.data?.message || (error as any)?.message;
        this.setState({
          submitting: false,
          error:
            typeof serverError === "string" && serverError.trim()
              ? serverError
              : "Could not leave the game. Your reason was kept so you can try again.",
        });
        return;
      }
      hideDialog();
    });
  };

  onCancel = () => {
    if (this.props.onCancel) this.props.onCancel();
    hideDialog();
  };

  render() {
    const { classes } = this.props;
    const { selected, message, error, submitting } = this.state;
    const otherSelected = Boolean(selected.other);

    return (
      <>
        <DialogContent className={classes.content}>
          <div className={classes.warningCard} role="status">
            <Typography variant="body2" className={classes.warningText}>
              Your current scores will be deleted when you leave. Choose why you are leaving (one or more reasons).
            </Typography>
          </div>

          <Typography variant="subtitle2" className={classes.sectionLabel} component="h2">
            Reason for leaving
          </Typography>

          <FormGroup className={classes.reasonGroup}>
            {LEAVE_REASON_OPTIONS.map(opt => {
              const checked = Boolean(selected[opt.key]);
              return (
                <FormControlLabel
                  key={opt.key}
                  className={`${classes.reasonRow} ${checked ? classes.reasonRowSelected : ""}`}
                  control={
                    <Checkbox
                      color="primary"
                      checked={checked}
                      onChange={this.toggle(opt.key)}
                      disabled={submitting}
                      className={classes.checkbox}
                    />
                  }
                  label={<span className={classes.reasonLabel}>{opt.label}</span>}
                />
              );
            })}
          </FormGroup>

          <TextField
            fullWidth
            multiline
            rows={2}
            margin="normal"
            variant="outlined"
            label={otherSelected ? "Message (required)" : "Message (optional)"}
            placeholder={otherSelected ? "Tell the organiser why you are leaving" : "Optional note for the organiser"}
            value={message}
            onChange={this.onMessage}
            disabled={submitting}
            required={otherSelected}
            error={Boolean(error) && otherSelected && !message.trim()}
            inputProps={{ maxLength: MAX_LEAVE_MESSAGE_LENGTH }}
            helperText={`${message.length}/${MAX_LEAVE_MESSAGE_LENGTH}`}
            className={classes.messageField}
          />

          {error ? (
            <Typography color="error" variant="body2" className={classes.errorText} role="alert">
              {error}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <div className={classes.actionsInner}>
            <Button onClick={this.onCancel} className={classes.cancelButton} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={this.onConfirm}
              variant="contained"
              color="primary"
              disabled={submitting}
              className={classes.confirmButton}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {submitting ? "Leaving…" : "Leave game"}
            </Button>
          </div>
        </DialogActions>
      </>
    );
  }
}

const createLeaveRequestId = () => {
  const browserCrypto = typeof window !== "undefined" ? (window.crypto as any) : undefined;
  if (browserCrypto?.randomUUID) return browserCrypto.randomUUID();
  if (browserCrypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    browserCrypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map(value => value.toString(16).padStart(2, "0"))
      .join("");
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;
};

const styles = (theme: Theme) =>
  createStyles({
    content: {
      padding: theme.spacing(2, 2.5, 1),
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(2, 3, 1),
      },
    },
    warningCard: {
      backgroundColor: "rgba(254, 243, 199, 0.65)",
      border: "1px solid rgba(245, 158, 11, 0.35)",
      borderRadius: 8,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
    },
    warningText: {
      color: "#92400e",
      lineHeight: 1.45,
      fontWeight: 500,
    },
    sectionLabel: {
      marginBottom: theme.spacing(1),
      color: "#0f172a",
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    reasonGroup: {
      margin: 0,
      gap: theme.spacing(0.5),
    },
    reasonRow: {
      margin: 0,
      marginLeft: 0,
      marginRight: 0,
      borderRadius: 8,
      border: "1px solid rgba(226, 232, 240, 0.95)",
      backgroundColor: "#ffffff",
      paddingRight: theme.spacing(1.5),
      paddingTop: 2,
      paddingBottom: 2,
      transition: theme.transitions.create(["background-color", "border-color"], {
        duration: theme.transitions.duration.shortest,
      }),
      "&:hover": {
        backgroundColor: "rgba(241, 245, 249, 0.7)",
      },
      "& .MuiFormControlLabel-label": {
        flex: 1,
      },
    },
    reasonRowSelected: {
      borderColor: "rgba(37, 99, 235, 0.35)",
      backgroundColor: "rgba(239, 246, 255, 0.85)",
      "&:hover": {
        backgroundColor: "rgba(239, 246, 255, 0.95)",
      },
    },
    checkbox: {
      paddingTop: 8,
      paddingBottom: 8,
    },
    reasonLabel: {
      fontSize: "0.9375rem",
      color: "#1e293b",
      lineHeight: 1.35,
    },
    messageField: {
      marginTop: theme.spacing(1.5),
      marginBottom: 0,
    },
    errorText: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    actions: {
      margin: 0,
      padding: theme.spacing(1, 2, 2),
      borderTop: "1px solid rgba(226, 232, 240, 0.92)",
      backgroundColor: "#ffffff",
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(1.5, 3, 2.5),
      },
    },
    actionsInner: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: theme.spacing(1),
      width: "100%",
    },
    cancelButton: {
      color: "#dc2626",
      fontWeight: 500,
      textTransform: "none",
      minWidth: 88,
    },
    confirmButton: {
      textTransform: "none",
      fontWeight: 600,
      minWidth: 120,
      boxShadow: "none",
      "&:active": {
        transform: "scale(0.98)",
      },
    },
  });

export default withStyles(styles)(LeaveGameReasonDialog);
